import { useState, useEffect, useCallback, useMemo } from "react";
import { Navigate } from "react-router-dom";
import api from "@/lib/api";
import { useLocationScope } from "@/context/LocationContext";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import SpotTile from "@/components/SpotTile";
import StructureBlock from "@/components/StructureBlock";
import CheckInModal from "@/components/CheckInModal";
import CheckOutModal from "@/components/CheckOutModal";
import { Loader2, Search, Wifi, MapPin, AlertTriangle } from "lucide-react";
import TicketSearch from "@/components/TicketSearch";

const CELL = 66; // px per grid cell on the visual map

const STATUS_FILTERS = ["All", "Available", "Occupied", "Reserved", "OutOfService"];

export default function LiveBoard() {
  const { selectedLot, isAll, loaded } = useLocationScope();
  const { user } = useAuth();
  const isManagerPlus = ["owner", "manager"].includes(user?.role);
  const lotId = isAll ? null : selectedLot;
  const [board, setBoard] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [checkIn, setCheckIn] = useState(null); // spot
  const [checkOut, setCheckOut] = useState(null); // {spot, session}

  useEffect(() => {
    api.get("/settings").then(({ data }) => setSettings(data));
  }, []);

  const loadBoard = useCallback(async () => {
    if (!lotId) return;
    const { data } = await api.get(`/lots/${lotId}/board`);
    setBoard(data);
    setLoading(false);
  }, [lotId]);

  useEffect(() => {
    setLoading(true);
    loadBoard();
    const t = setInterval(loadBoard, 15000); // live refresh
    return () => clearInterval(t);
  }, [loadBoard]);

  const matches = useCallback(
    (s) => {
      if (statusFilter !== "All" && s.status !== statusFilter) return false;
      if (search && !String(s.spotNumber).includes(search.trim())) return false;
      return true;
    },
    [statusFilter, search]
  );

  const hasLayout = useMemo(
    () => !!board?.spots?.some((s) => s.posX != null && s.posY != null),
    [board]
  );

  const counts = board?.counts;

  const handleTileClick = (spot, session) => {
    if (spot.status === "Available") {
      setCheckIn(spot);
    } else if (session) {
      setCheckOut({ spot, session });
    }
  };

  if (loaded && isAll && isManagerPlus) {
    return <Navigate to="/dashboard" replace />;
  }
  if (loaded && isAll && !isManagerPlus) {
    return (
      <div className="flex h-[70vh] flex-col items-center justify-center gap-3 text-slate-400">
        <MapPin className="h-10 w-10 text-[#A0148C]" />
        <p className="text-lg">Select a location above to view its board.</p>
      </div>
    );
  }

  if (loading && !board) {
    return (
      <div className="flex h-[80vh] items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-[#A0148C]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="border-b border-slate-800 bg-slate-950/95">
        <div className="px-4 py-4 md:px-8">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-4">
              <div>
                <h1 className="font-heading text-2xl font-black tracking-tight text-slate-50">
                  {board?.lot?.name || "Live Board"}
                </h1>
                {board?.lot && (
                  <div className="flex flex-wrap items-center gap-3 text-sm text-slate-400">
                    <span className="flex items-center gap-1">
                      <MapPin className="h-4 w-4" /> {board.lot.address}
                    </span>
                    {board.lot.freeWifi && (
                      <span className="flex items-center gap-1 text-emerald-400">
                        <Wifi className="h-4 w-4" /> Free WiFi
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>

            <TicketSearch />

            {/* Live counts */}
            {counts && (
              <div className="flex flex-wrap items-center gap-2">
                <div
                  data-testid="available-count"
                  className="rounded-lg border border-emerald-500/40 bg-emerald-950/40 px-4 py-2"
                >
                  <span className="font-data text-2xl font-bold text-emerald-300">
                    {counts.available}
                  </span>
                  <span className="ml-1 text-sm text-slate-400">of {counts.total} available</span>
                </div>
                <Stat label="Occupied" value={counts.occupied} color="text-red-300" />
                <Stat label="Reserved" value={counts.reserved} color="text-amber-300" />
                {counts.overstays > 0 && (
                  <div
                    data-testid="overstay-count"
                    className="overstay-pulse flex items-center gap-1 rounded-lg border border-red-500 bg-red-950/60 px-4 py-2 text-red-300"
                  >
                    <AlertTriangle className="h-4 w-4" />
                    <span className="font-data text-xl font-bold">{counts.overstays}</span>
                    <span className="text-sm">overstay</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Filters */}
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
              <Input
                data-testid="spot-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Find spot #"
                className="h-11 w-40 border-slate-700 bg-slate-900 pl-9"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {STATUS_FILTERS.map((f) => (
                <button
                  key={f}
                  data-testid={`filter-${f}`}
                  onClick={() => setStatusFilter(f)}
                  className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                    statusFilter === f
                      ? "bg-[#A0148C] text-white"
                      : "bg-slate-900 text-slate-400 hover:bg-slate-800"
                  }`}
                >
                  {f === "OutOfService" ? "Out of Service" : f}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Visual yard map */}
      {hasLayout ? (
        <div className="overflow-auto p-4 md:p-8">
          <div
            data-testid="yard-map"
            className="relative inline-grid gap-1.5 rounded-xl border border-slate-800 bg-slate-950/60 p-4"
            style={{
              gridTemplateColumns: `repeat(${board?.lot?.mapCols || 12}, ${CELL}px)`,
              gridAutoRows: `${CELL}px`,
            }}
          >
            {(board.structures || []).map((st) => (
              <StructureBlock key={st.id} structure={st} />
            ))}
            {board.spots
              .filter((s) => s.posX != null && s.posY != null)
              .map((spot) => (
                <div
                  key={spot.id}
                  style={{ gridColumnStart: spot.posX + 1, gridRowStart: spot.posY + 1 }}
                >
                  <SpotTile
                    spot={spot}
                    session={board.sessionsBySpot[spot.id]}
                    onClick={handleTileClick}
                    compact
                    orientation={spot.orientation}
                    dim={!matches(spot)}
                  />
                </div>
              ))}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-3 p-4 sm:grid-cols-4 md:grid-cols-6 md:p-8 lg:grid-cols-8 xl:grid-cols-10">
          {board.spots.filter(matches).map((spot) => (
            <SpotTile
              key={spot.id}
              spot={spot}
              session={board.sessionsBySpot[spot.id]}
              onClick={handleTileClick}
            />
          ))}
          {board.spots.filter(matches).length === 0 && (
            <p className="col-span-full py-12 text-center text-slate-500">No spots match your filter.</p>
          )}
        </div>
      )}

      {checkIn && (
        <CheckInModal
          open={!!checkIn}
          spot={checkIn}
          lotId={lotId}
          lot={board?.lot}
          settings={settings}
          onClose={() => setCheckIn(null)}
          onDone={loadBoard}
        />
      )}
      {checkOut && (
        <CheckOutModal
          open={!!checkOut}
          spot={checkOut.spot}
          session={checkOut.session}
          lot={board?.lot}
          settings={settings}
          onClose={() => setCheckOut(null)}
          onDone={loadBoard}
        />
      )}
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="rounded-lg border border-slate-700 bg-slate-900 px-4 py-2">
      <span className={`font-data text-2xl font-bold ${color}`}>{value}</span>
      <span className="ml-1 text-sm text-slate-400">{label}</span>
    </div>
  );
}
